import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faTrashAlt,
  faHeart,
  faUserCheck,
  faUserPlus,
  faUnlockAlt,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import Swal from 'sweetalert2';
import MainNavbar from "../MainNavbar";
import Notifications from "./Notifications";
import "./Inbox.css";
import { useContext } from "react";
import AlertContext from "../../../contexts/AlertContext";

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState({
    userId: "",
    gender: "",
    name: "",
  });
  const [activeTab, setActiveTab] = useState("notifications");
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [acceptedProfiles, setAcceptedProfiles] = useState([]);
  const [followingProfiles, setFollowingProfiles] = useState([]);
  const [shortlistedUsers, setShortlistedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const { showAlert } = useContext(AlertContext);
  const [blockedUsers, setBlockedUsers] = useState([]);


  // === FETCH DATA FUNCTIONS ===
  const fetchAcceptedCount = async () => {
    try {
      const res = await axios.get("https://back.varankal.com/user/accepted-count", {
        withCredentials: true,
      });
      setAcceptedCount(res.data.acceptedCount || 0);
    } catch (err) {
      console.error("Error fetching accepted count:", err);
    }
  };

  const fetchFollowingCount = async () => {
    try {
      const res = await axios.get("https://back.varankal.com/user/following-count", {
        withCredentials: true,
      });
      setFollowingCount(res.data.followingCount || 0);
    } catch (err) {
      console.error("Error fetching following count:", err);
    }
  };

  const fetchAcceptedProfiles = async () => {
    try {
      const res = await axios.get("https://back.varankal.com/user/accepted-profiles", {
        withCredentials: true,
      });
      setAcceptedProfiles(res.data.followers || []);
    } catch (err) {
      console.error("Error fetching accepted profiles:", err);
    }
  };

  const fetchFollowingProfiles = async () => {
    try {
      const res = await axios.get("https://back.varankal.com/user/following-profiles", {
        withCredentials: true,
      });
      setFollowingProfiles(res.data.following || []);
    } catch (err) {
      console.error("Error fetching following profiles:", err);
    }
  };

  const fetchShortlistedUsers = async () => {
    try {
      const res = await axios.get("https://back.varankal.com/auth/shortlisted", {
        withCredentials: true,
      });
      setShortlistedUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching shortlisted users:", err);
      setError("Failed to load shortlisted users");
    }
  };
  const fetchBlockedUsers = async () => {
    try {
      const res = await axios.get("https://back.varankal.com/auth/blocked-users", {
        withCredentials: true,
      });
      setBlockedUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching blocked users:", err);
    }
  };
  useEffect(() => {
    fetchBlockedUsers();
  }, []);


  // === HANDLE PROFILE CLICK (VIEW PROFILE ACCESS CHECK) ===
  const handleProfileClick = async (profileId) => {
    try {
      const res = await axios.get(
        `https://back.varankal.com/user/view-profile/${profileId}`,
        { withCredentials: true }
      );

      if (res.data.profile) {
        navigate(`/userpage/${profileId}`);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        alert(err.response.data.error || "Upgrade your plan to view more profiles.");
      } else {
        showAlert("An error occurred while opening this profile.");
      }
    }
  };

  // === UNSHORTLIST FUNCTION ===
  const unshortlistUser = async (profileId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to remove this user from your shortlist?"
    );
    if (!confirmDelete) return;

    try {
      const res = await axios.post(
        `https://back.varankal.com/auth/unshortlist/${profileId}`,
        { currentUserId: currentUser._id || currentUser.userId },
        { withCredentials: true }
      );

      console.log("✅ Unshortlisted:", res.data);
      setShortlistedUsers((prev) =>
        prev.filter((user) => user._id !== profileId && user.userId !== profileId)
      );
    } catch (err) {
      console.error("Error unshortlisting user:", err);
      showAlert("Failed to unshortlist user.");
    }
  };

  // === INITIAL FETCH ===
  useEffect(() => {
    fetchAcceptedCount();
    fetchFollowingCount();
    fetchAcceptedProfiles();
    fetchFollowingProfiles();
    fetchShortlistedUsers();
  }, []);
  // === RENDER HELPER ===
  const renderProfileList = (profiles, emptyText, showUnshortlist = false, isBlockedList = false) => {
    const filtered = profiles.filter((p) =>
      p.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (filtered.length === 0) return <p>{emptyText}</p>;

    return (
      <ul className="profileStatsSection-list">
        {filtered.map((p, index) => (
          <li
            key={index}
            className="profileStatsSection-listItem"
            onClick={() => !isBlockedList && handleProfileClick(p._id || p.userId)} // Blocked users not clickable
          >
            <img
              src={
                p.profilePicture
                  ? `data:image/jpeg;base64,${p.profilePicture}`
                  : "/default-avatar.png"
              }
              alt={p.name}
              className="profileStatsSection-listImg"
            />
            <span className="profileStatsSection-listText">
              {p.name} {p.age ? `- ${p.age}` : ""}
            </span>

            {/* Show action button depending on list type */}
            {isBlockedList ? (
              <button
                className="profileStatsSection-removeBtn"
                onClick={(e) => {
                  e.stopPropagation();

                  Swal.fire({
                    title: "Unblock User?",
                    text: "Are you sure you want to unblock this user?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Yes, Unblock",
                  }).then(async (result) => {
                    if (result.isConfirmed) {
                      try {
                        await axios.post(
                          `https://back.varankal.com/auth/unblock/${p._id || p.userId}`,
                          {},
                          { withCredentials: true }
                        );
                        Swal.fire("Unblocked!", "User has been unblocked.", "success");
                        fetchBlockedUsers(); // Refresh blocked users list
                      } catch (err) {
                        console.error("Error unblocking user:", err);
                        Swal.fire("Error!", "Failed to unblock user.", "error");
                      }
                    }
                  });
                }}
              >
                <FontAwesomeIcon icon={faUnlockAlt} />
              </button>
            ) : showUnshortlist ? (
              <button
                className="profileStatsSection-removeBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  unshortlistUser(p._id || p.userId);
                }}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            ) : null}

          </li>
        ))}
      </ul>
    );
  };

  // === MAIN RETURN ===
  return (
    <>
      <MainNavbar />
      <div id="profile-details" className="profileStatsSection-container">
        {/* === Tabs === */}
        <div className="profileStatsSection-statsTabs">
          <div
            className={`profileStatsSection-tab ${activeTab === "notifications" ? "active" : ""
              }`}
            onClick={() => setActiveTab("notifications")}
          >
            <FontAwesomeIcon icon={faBell} style={{ marginRight: 8 }} />
            Notifications
          </div>

          <div
            className={`profileStatsSection-tab ${activeTab === "accepted" ? "active" : ""
              }`}
            onClick={() => setActiveTab("accepted")}
          >
            <FontAwesomeIcon icon={faUserCheck} style={{ marginRight: 8 }} />
            Accepted ({acceptedCount})
          </div>

          <div
            className={`profileStatsSection-tab ${activeTab === "requested" ? "active" : ""
              }`}
            onClick={() => setActiveTab("requested")}
          >
            <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: 8 }} />
            Requested ({followingCount})
          </div>

          <div
            className={`profileStatsSection-tab ${activeTab === "shortlist" ? "active" : ""
              }`}
            onClick={() => setActiveTab("shortlist")}
          >
            <FontAwesomeIcon icon={faHeart} style={{ marginRight: 8 }} />
            Shortlisted ({shortlistedUsers.length})
          </div>
          <div
            className={`profileStatsSection-tab ${activeTab === "blocked" ? "active" : ""}`}
            onClick={() => setActiveTab("blocked")}
          >
            <FontAwesomeIcon icon={faBan} style={{ marginRight: 8 }} />
            Blocked ({blockedUsers.length})
          </div>
        </div>

        {/* === Tab Contents === */}
        <div className="profileStatsSection-tabContent">
          {activeTab === "notifications" && (
            <div className="tabContent">

              <Notifications />
            </div>
          )}

          {activeTab === "accepted" && (
            <div className="tabContent">
              <h4>Accepted Requests</h4>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="profileStatsSection-overlaySearch"
              />
              {renderProfileList(acceptedProfiles, "No accepted requests found.")}
            </div>
          )}

          {activeTab === "requested" && (
            <div className="tabContent">
              <h4>Sent Requests</h4>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="profileStatsSection-overlaySearch"
              />
              {renderProfileList(followingProfiles, "No requested profiles found.")}
            </div>
          )}

          {activeTab === "shortlist" && (
            <div className="tabContent">
              <h4>Shortlisted Profiles</h4>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="profileStatsSection-overlaySearch"
              />
              {renderProfileList(
                shortlistedUsers,
                "No shortlisted users yet.",
                true
              )}
            </div>
          )}
          {activeTab === "blocked" && (
            <div className="tabContent">
              <h4>Blocked Users</h4>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="profileStatsSection-overlaySearch"
              />
              {renderProfileList(blockedUsers, "No blocked users yet.", false, true)}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Profile;
